import * as React from 'react';
import { useFormikContext } from 'formik';
import { OBShippingContactInfo, StrictSetFieldValue } from '../../../../types';
import {
  SingleSelect,
  TextInput,
} from '../../../../widgets/FormikInputFieldV2';
import { useSelector } from 'react-redux';
import {
  getContactOption,
  onboardingSelector,
} from '../../../../reducers/onboarding';
import { formatOptionLabel, PHONE_TYPE_OPTIONS } from '../../../../constants';
import { Panel } from '@rtkwlf/ui';
import styled from 'styled-components';
import PhoneNumberInput from '../../../../widgets/PhoneNumberInput';

const useContactOptions = () => {
  const contacts = useSelector(onboardingSelector.getContacts);
  const options = React.useMemo(
    () =>
      Object.entries(contacts)
        .filter(([, contact]) => !contact.isDL)
        .map(([uuid, contact]) => getContactOption(uuid, contact, false)),
    [contacts],
  );

  return { contacts, options };
};

const RadioContainer = styled.div`
  display: flex;
  margin-bottom: 15px;
  align-items: center;
`;

const SiteSelectContainer = styled.div`
  width: 100%;
  margin-left: 5px;
`;

const ContactLabel = styled.div`
  margin-left: 5px;
`;

const ShippingContactPanel: typeof Panel = styled(Panel)`
  margin-left: 0px;
  margin-right: 0px;
  margin-top: 20px;
`;

/**
 * A collection of shipping contact-related form input fields.
 * Allows toggling between using existing contact & adding new contact.
 * Must be used within a formik context with the following fields:
 *  - isShippingToNewContact
 *  - shippingContactId
 *  - shippingContactFirstName
 *  - shippingContactLastName
 *  - shippingContactEmail
 *  - shippingContactPhoneNumber
 *  - shippingContactPhoneType
 */
export const ShippingContactFormInputs: React.FunctionComponent = () => {
  const formikContext = useFormikContext<OBShippingContactInfo>();
  const { values, setFieldTouched, isSubmitting } = formikContext;
  const setFieldValue: StrictSetFieldValue<OBShippingContactInfo> =
    formikContext.setFieldValue;
  const { shippingContactId, isShippingToNewContact } = values;

  const { contacts, options } = useContactOptions();

  React.useEffect(() => {
    if (!isShippingToNewContact) {
      // populate the alternative contact with the selected existing contact
      const existingContact = contacts[shippingContactId];
      if (existingContact) {
        setFieldValue('shippingContactFirstName', existingContact.firstName);
        setFieldValue('shippingContactLastName', existingContact.lastName);
        setFieldValue('shippingContactEmail', existingContact.email);
        setFieldValue(
          'shippingContactPhoneNumber',
          existingContact.primaryNumber,
        );
        setFieldValue(
          'shippingContactPhoneType',
          existingContact.primaryNumberType,
        );
      }
    }
  }, [
    setFieldValue,
    setFieldTouched,
    contacts,
    shippingContactId,
    isShippingToNewContact,
  ]);

  const onShipToNewContact = React.useCallback(() => {
    setFieldValue('isShippingToNewContact', true);
    setFieldValue('shippingContactId', '');

    // clear the alternative address
    setFieldValue('shippingContactFirstName', '');
    setFieldTouched('shippingContactFirstName', false);
    setFieldValue('shippingContactLastName', '');
    setFieldTouched('shippingContactLastName', false);
    setFieldValue('shippingContactEmail', '');
    setFieldTouched('shippingContactEmail', false);
    setFieldValue('shippingContactPhoneNumber', '');
    setFieldTouched('shippingContactPhoneNumber', false);
  }, [setFieldTouched, setFieldValue]);

  const onShipToExistingContact = React.useCallback(() => {
    setFieldValue('shippingContactFirstName', '');
    setFieldTouched('shippingContactFirstName', false);
    setFieldValue('shippingContactLastName', '');
    setFieldTouched('shippingContactLastName', false);
    setFieldValue('shippingContactEmail', '');
    setFieldTouched('shippingContactEmail', false);
    setFieldValue('shippingContactPhoneNumber', '');
    setFieldTouched('shippingContactPhoneNumber', false);
    setFieldValue('isShippingToNewContact', false);
  }, [setFieldTouched, setFieldValue]);

  return (
    <ShippingContactPanel title="Shipping Contact" titleLevel={5}>
      <RadioContainer className="radio-button-group">
        <input
          type="radio"
          name="isNotShippingToNewContact"
          checked={!isShippingToNewContact}
          onChange={onShipToExistingContact}
          disabled={isSubmitting}
        />
        <SiteSelectContainer>
          <SingleSelect
            name="shippingContactId"
            id="selectShippingContactId"
            placeholder="Choose a contact"
            options={options}
            className="radio-button-select"
            isDisabled={isShippingToNewContact || isSubmitting}
            formatOptionLabel={formatOptionLabel}
          />
        </SiteSelectContainer>
      </RadioContainer>

      <div className="radio-button-group">
        <RadioContainer>
          <input
            type="radio"
            name="isShippingToNewContact"
            checked={isShippingToNewContact}
            onChange={onShipToNewContact}
            disabled={isSubmitting}
          />
          <ContactLabel>Another contact</ContactLabel>
        </RadioContainer>
        <TextInput
          name="shippingContactFirstName"
          label="First Name"
          className="mt-3"
          disabled={!isShippingToNewContact || isSubmitting}
          required
        />
        <TextInput
          name="shippingContactLastName"
          label="Last Name"
          disabled={!isShippingToNewContact || isSubmitting}
          required
        />
        <TextInput
          name="shippingContactEmail"
          label="Email Address"
          disabled={!isShippingToNewContact || isSubmitting}
          required
        />
        <div className="d-flex">
          <PhoneNumberInput
            name="shippingContactPhoneNumber"
            required
            disabled={!isShippingToNewContact || isSubmitting}
          />
          <div style={{ width: 150, marginLeft: '1rem', marginTop: '1.5rem' }}>
            <SingleSelect
              name="shippingContactPhoneType"
              id="selectShippingContactPhoneType"
              options={PHONE_TYPE_OPTIONS}
              isDisabled={!isShippingToNewContact || isSubmitting}
            />
          </div>
        </div>
      </div>
    </ShippingContactPanel>
  );
};
